import type { Embed } from "../domain/askNoticeboard/embeddingMatcher";
import type { EmbedRequest, EmbedResponse } from "./embeddingWorker";

/** The first call also downloads the model. Later calls only embed. */
const MODEL_LOAD_TIMEOUT_MS = 60_000;
const EMBED_TIMEOUT_MS = 15_000;

/** The bits of a Worker this client needs. Tests pass a stand-in. */
export type EmbedWorkerLike = {
  postMessage: (message: EmbedRequest) => void;
  terminate: () => void;
  onmessage: ((event: MessageEvent<EmbedResponse>) => void) | null;
  onerror: ((event: { message?: string }) => void) | null;
};

/**
 * Starts the model in a Web Worker and returns its embed function, once the model
 * has loaded. The worker downloads the library and model on demand, so they stay
 * out of the bundle; the browser's cache keeps the model for later visits. Only the
 * model is downloaded: questions never leave the browser. Never called in Jest.
 */
export async function loadModelEmbed(): Promise<Embed> {
  const { startEmbeddingWorker } = await import("./embeddingWorker");
  const worker = startEmbeddingWorker();
  const embed = createEmbedClient(worker as EmbedWorkerLike);
  try {
    await embed([]); // Loads the model, so a failed load fails here
  } catch (error) {
    worker.terminate();
    throw error;
  }
  return embed;
}

/**
 * Turns worker messages into an embed function. A worker error or a timeout fails
 * every call still waiting and every call after that, so a dead worker cannot hang
 * the next question.
 */
export function createEmbedClient(
  worker: EmbedWorkerLike,
  embedTimeoutMs = EMBED_TIMEOUT_MS
): Embed {
  const waiting = new Map<number, { resolve: (vectors: number[][]) => void; reject: (error: Error) => void }>();
  let nextId = 0;
  let failed: Error | null = null;

  const fail = (error: Error) => {
    if (!failed) failed = error;
    waiting.forEach(({ reject }) => reject(failed as Error));
    waiting.clear();
    worker.terminate();
  };

  worker.onmessage = ({ data }: MessageEvent<EmbedResponse>) => {
    const request = waiting.get(data.id);
    if (!request) return;
    waiting.delete(data.id);
    if ("error" in data) request.reject(new Error(data.error));
    else request.resolve(data.vectors);
  };
  worker.onerror = (event) => {
    fail(new Error(event.message || "The model's worker failed"));
  };

  return (texts) =>
    new Promise((resolve, reject) => {
      if (failed) {
        reject(failed);
        return;
      }
      const id = nextId++;
      const timeoutMs = texts.length === 0 ? MODEL_LOAD_TIMEOUT_MS : embedTimeoutMs;
      const timer = setTimeout(() => {
        if (!waiting.has(id)) return;
        fail(new Error("The model took too long"));
      }, timeoutMs);
      waiting.set(id, {
        resolve: (vectors) => {
          clearTimeout(timer);
          resolve(vectors);
        },
        reject: (error) => {
          clearTimeout(timer);
          reject(error);
        },
      });
      try {
        const request: EmbedRequest = { id, texts };
        worker.postMessage(request);
      } catch (error) {
        fail(error instanceof Error ? error : new Error(String(error)));
      }
    });
}
